import { useTranslation } from "react-i18next";
import { memo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/utils/cn";
import { TONE_ACCENT_FG_VAR, TONE_ACCENT_VAR } from "../grid.constants";
import type { IWidgetDefinition, TWidgetSizeKey } from "../types";
import { WidgetErrorBoundary } from "./WidgetErrorBoundary";

interface WidgetCardProps {
  definition: IWidgetDefinition;
  size: TWidgetSizeKey;
  isEditing: boolean;
  /** `lifted` is the copy that follows the pointer inside the drag overlay. */
  variant?: "default" | "lifted";
  className?: string;
}

/**
 * The identity row: which app this is, and what it is called.
 *
 * One implementation for every size, taking no size prop at all, because the
 * chip and the name are the two things on a widget that must never vary. A
 * canvas of ten widgets is only legible as one system if the mark is the same
 * mark everywhere — the moment a 28px chip becomes a 20px one on the small
 * tiles, the eye has to re-learn the row. An `xs` card sacrifices its content,
 * not its identity: at 104px tall the header costs 44px and the figure still
 * has room, which is the trade every phone home screen makes too.
 *
 * `showChevron` is the one thing that varies, and it varies on whether the
 * header is *itself* the link rather than on size. On an `xs` card the whole
 * tile is the anchor, so a chevron pointing at a subset of it would be telling
 * the user to aim at the wrong thing — and it would take ~22px from a name that
 * has 92px to live in once the canvas drops to four columns.
 */
const WidgetCardHeader = ({
  definition,
  showChevron,
}: {
  definition: IWidgetDefinition;
  showChevron: boolean;
}) => {
  const { t } = useTranslation(["common", "company"]);
  const Icon = definition.icon;

  return (
    <>
      {/* Same size-7 box `.widget-app-chip` used, kept for the header's
          height budget — see the file-level comment. Only the badge is gone;
          the glyph is bigger now that it isn't sitting inside one. */}
      <span
        className="widget-app-icon grid size-7 shrink-0 place-items-center"
        aria-hidden
      >
        <Icon size={18} />
      </span>
      {/* `title` for the case the ellipsis is doing real work: an `xs` card
          scales to a single column below 620px of canvas, which leaves the name
          about 56px once the chip has taken its 28. Assistive tech already gets
          the full name from the link's `aria-label`; this is the equivalent for
          someone looking at it. */}
      <span
        title={t(definition.name)}
        className="min-w-0 flex-1 truncate text-widget-title font-semibold text-foreground"
      >
        {t(definition.name)}
      </span>
      {showChevron && (
        <ChevronRight
          size={14}
          aria-hidden
          className="shrink-0 text-muted-foreground transition-transform group-hover/card:translate-x-0.5 group-hover/card:text-foreground"
        />
      )}
    </>
  );
};

/**
 * The card itself — header, body, and the tone that ties the two together.
 *
 * Presentational on purpose: the same component renders in the grid and inside
 * the drag overlay, so what you drag is pixel-identical to what you picked up
 * rather than a stand-in that almost matches.
 *
 * Two link shapes, chosen by how much else is inside the card:
 *
 * At every size from `sm` up the header row is the deep link, not a small
 * "Open" affordance at the end of it — a phone widget is one big target; a 40px
 * text link inside a card is a desktop habit. It stops at the header because
 * several widget bodies contain links of their own, and an anchor inside an
 * anchor is invalid markup with genuinely unpredictable behaviour.
 *
 * At `xs` the whole tile is the anchor instead. That card's body is a single
 * figure with nothing clickable in it, so the reason to stop at the header does
 * not apply, and a ~170px tile is exactly the object a thumb aims at whole.
 *
 * `xs` differs in that, in its padding, and in nothing else. It used to be a
 * separate `MiniWidgetCard` that dropped the header entirely and floated the
 * chip as a 20px corner badge over the content, with the widget's name demoted
 * to a caption under the figure. It bought the figure about a third more height
 * and cost the canvas its one invariant: the same widget carried a different
 * mark, at a different size, in a different place, depending on how big the
 * user had made it, and the name moved from above the number to below it. The
 * height is worth less than the consistency.
 *
 * Memoised: its props are all stable when nothing about the widget has changed,
 * so during a drag or a resize the other cards re-render zero times.
 */
const WidgetCardComponent = ({
  definition,
  size,
  isEditing,
  variant = "default",
  className,
}: WidgetCardProps) => {
  const { t } = useTranslation(["common", "company"]);
  const Body = definition.component;
  const isLinked = Boolean(definition.link) && !isEditing;
  const isMini = size === "xs";

  const style = {
    ["--widget-accent" as string]: TONE_ACCENT_VAR[definition.tone],
    ["--widget-accent-fg" as string]: TONE_ACCENT_FG_VAR[definition.tone],
  };
  const cardClassName = cn(
    "widget-card flex h-full min-h-0 flex-col overflow-hidden",
    variant === "lifted" && "widget-card-lifted",
    className,
  );

  const header = (
    <WidgetCardHeader
      definition={definition}
      showChevron={isLinked && !isMini}
    />
  );

  // Tighter on all four sides at `xs`. The header is the same header, so the
  // only room left to find is in the padding around it: 44px of chrome and a
  // 28px figure leave about 20px spare in a 104px row, which is enough air and
  // not enough to spend on 16px gutters.
  const headerPadding = isMini ? "px-3 pt-2.5 pb-1.5" : "px-4 pt-3 pb-2";
  const bodyPadding = isMini ? "px-3 pb-3" : "px-4 pb-3.5";
  // Only `xs` gets its vertical alignment imposed from out here. Every other
  // size hands its body a plain block of the remaining height and lets the
  // widget lay itself out inside it — several of them centre, several fill —
  // and reaching in to anchor those from the card would fight layouts that
  // already work. The `xs` body is a bare figure with no opinion of its own, so
  // it needs one: bottom, which puts mark and name at the top of the tile and
  // the number at the bottom, the way a phone's small widgets read.

  const content = (
    <>
      {isLinked && !isMini ? (
        <Link
          to={definition.link!.to}
          aria-label={t("common:dashboard_grid.open_widget", {
            widget: t(definition.name),
          })}
          className={cn(
            "widget-focus-inset relative z-1 flex shrink-0 items-center gap-2 rounded-t-widget",
            headerPadding,
          )}
        >
          {header}
        </Link>
      ) : (
        <div
          className={cn(
            "relative z-1 flex shrink-0 items-center gap-2",
            headerPadding,
          )}
        >
          {header}
        </div>
      )}

      <div
        data-widget-body
        className={cn(
          "relative z-1 min-h-0 flex-1",
          isMini && "flex flex-col justify-end",
          bodyPadding,
        )}
      >
        <WidgetErrorBoundary widgetName={t(definition.name)}>
          <Body size={size} isEditing={isEditing} />
        </WidgetErrorBoundary>
      </div>
    </>
  );

  if (isLinked && isMini) {
    return (
      <Link
        to={definition.link!.to}
        aria-label={t("common:dashboard_grid.open_widget", {
          widget: t(definition.name),
        })}
        style={style}
        className={cn(cardClassName, "widget-focus-inset")}
      >
        {content}
      </Link>
    );
  }

  return (
    <div style={style} className={cardClassName}>
      {content}
    </div>
  );
};

export const WidgetCard = memo(WidgetCardComponent);
